import React, { useState, useEffect, Fragment, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Tab,
  Tabs,
  Badge,
  Avatar,
  CircularProgress,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  Drawer,
  FormGroup,
  Alert
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import StopIcon from '@mui/icons-material/Stop';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import MessageIcon from '@mui/icons-material/Message';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import GroupsIcon from '@mui/icons-material/Groups';
import WifiIcon from '@mui/icons-material/Wifi';
import websocketService from '../services/websocketService';
import { APP_VERSION, WIDGET_VERSION, APP_DESCRIPTION } from '../version';
import { useLocation, useNavigate } from 'react-router-dom';
import DescriptionIcon from '@mui/icons-material/Description';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import RefreshIcon from '@mui/icons-material/Refresh';
import GavelIcon from '@mui/icons-material/Gavel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BlockIcon from '@mui/icons-material/Block';
import DoNotDisturbOffIcon from '@mui/icons-material/DoNotDisturbOff';
import SecurityIcon from '@mui/icons-material/Security';
import { defaultClosedBookPolicy, mergePolicy } from '../security';
import {
  buildAuditExportPayload,
  downloadAuditCsv,
  downloadAuditXlsx,
  downloadAuditPdf
} from '../utils/proctoringAuditExport';
/** Stable key for deduping live WS violation bursts (uses clientViolationId when present). */
function violationEventDedupeKey(data) {
  if (!data || typeof data !== 'object') return '';
  if (data.clientViolationId) return `cid:${String(data.clientViolationId)}`;
  const uid = data.userId ?? data.studentId ?? data.user_id ?? '';
  const type = data.type ?? '';
  const ts = data.timestamp != null ? String(data.timestamp) : '';
  return `${String(uid)}|${String(type)}|${ts}`;
}

function normalizeViolation(v) {
  if (!v || typeof v !== 'object') return null;
  const userId = v.userId ?? v.user_id ?? v.studentId;
  return {
    ...v,
    id: v.id,
    userId: userId != null ? String(userId) : '',
    user_id: userId != null ? String(userId) : '',
    type: v.type || 'Unknown',
    severity: v.severity || 'medium',
    details: v.details || '',
    timestamp: v.timestamp,
    snapshot_url: v.snapshot_url ?? v.snapshotUrl
  };
}

function sortViolationsNewestFirst(arr) {
  return [...arr].sort((a, b) => {
    const ta = a?.timestamp ? new Date(a.timestamp).getTime() : 0;
    const tb = b?.timestamp ? new Date(b.timestamp).getTime() : 0;
    return tb - ta;
  });
}

function formatViolationTime(ts) {
  if (ts == null || ts === '') return '—';
  try {
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? String(ts) : d.toLocaleString();
  } catch {
    return String(ts);
  }
}

function buildFaceVerifyFailureViolation(userId, examId, confidence, ts = new Date()) {
  const uid = String(userId);
  const eid = String(examId);
  const tsMs = ts instanceof Date ? ts.getTime() : new Date(ts).getTime();
  const safeTs = Number.isFinite(tsMs) ? tsMs : Date.now();
  return {
    userId: uid,
    examId: eid,
    type: 'Face Verification Failed',
    details: confidence != null && Number(confidence) > 0
      ? `Face verification failed (confidence ${Number(confidence).toFixed(1)}%)`
      : 'Face verification failed',
    severity: 'high',
    timestamp: new Date(safeTs).toISOString(),
    clientViolationId: `face-verify:${uid}:${Math.floor(safeTs / 30000)}`
  };
}

const HEARTBEAT_CONNECTED_MS = 35000;

function isStudentConnected(userId, lastHeartbeats, disconnectedUserIds, nowMs = Date.now()) {
  const ts = lastHeartbeats[userId];
  return !disconnectedUserIds[userId] && !!ts && nowMs - ts < HEARTBEAT_CONNECTED_MS;
}

function mergeViolationLists(existing, incoming) {
  const merged = [];
  const seen = new Set();
  [...incoming, ...existing].forEach((raw) => {
    const v = normalizeViolation(raw);
    if (!v) return;
    const key = violationEventDedupeKey(v) || `id:${v.id ?? merged.length}`;
    if (seen.has(key)) return;
    seen.add(key);
    merged.push(v);
  });
  return sortViolationsNewestFirst(merged);
}

// Canvas component to render webcam frames
const CanvasFrameRenderer = ({ userId, frameData, frameKey }) => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    if (!canvasRef.current || !frameData) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Set canvas size to match image
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      
      // Draw image to canvas
      ctx.drawImage(img, 0, 0);
      console.log('[Admin][UI] ✅ Canvas frame rendered for userId:', userId, 'Size:', canvas.width, 'x', canvas.height);
      
      // Verify canvas has content - check multiple areas
      const sampleSize = 50;
      const centerX = Math.floor(canvas.width / 2);
      const centerY = Math.floor(canvas.height / 2);
      const imageData = ctx.getImageData(centerX - sampleSize/2, centerY - sampleSize/2, sampleSize, sampleSize);
      
      // Check if there are any non-black pixels (not all zeros)
      const nonBlackPixels = imageData.data.filter((val, idx) => idx % 4 !== 3 && val > 10); // Skip alpha channel, look for values > 10
      const hasContent = nonBlackPixels.length > 0;
      const avgBrightness = imageData.data.reduce((sum, val, idx) => idx % 4 === 3 ? sum : sum + val, 0) / (imageData.data.length * 0.75);
      
      console.log('[Admin][UI] Canvas content check:', {
        hasContent,
        avgBrightness: Math.round(avgBrightness),
        nonBlackPixels: nonBlackPixels.length,
        sampleArea: 'center',
        firstCenterPixels: Array.from(imageData.data.slice(0, 16))
      });
    };
    
    img.onerror = (err) => {
      console.error('[Admin][UI] ❌ Failed to load image for canvas:', err);
      console.error('[Admin][UI] Frame data preview (first 200):', frameData?.substring(0, 200));
      console.error('[Admin][UI] Frame data preview (last 200):', frameData?.substring(Math.max(0, frameData.length - 200)));
    };
    
    img.src = frameData;
  }, [userId, frameData, frameKey]);
  
  return (
    <canvas
      ref={canvasRef}
      style={{ 
        maxWidth: '100%', 
        maxHeight: '360px', 
        borderRadius: 8,
        display: 'block',
        backgroundColor: '#000',
        border: '2px solid red',
        zIndex: 1,
        margin: '0 auto',
        imageRendering: 'auto' // Ensure smooth rendering
      }}
    />
  );
};

const AdminDashboard = ({ onViolationsUpdate }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [violations, setViolations] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connectedUsers, setConnectedUsers] = useState({}); // eslint-disable-line no-unused-vars -- kept for WS subscription; registration UI uses registrationByUser
  const [violationCounts, setViolationCounts] = useState({});
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentFrames, setStudentFrames] = useState({});
  const [studentScreenFrames, setStudentScreenFrames] = useState({});
  const [frameKeys, setFrameKeys] = useState({}); // Track frame update keys for forcing re-render
  const canvasRefs = useRef({}); // Store canvas refs per userId
  const [lastHeartbeats, setLastHeartbeats] = useState({});
  const [disconnectedUserIds, setDisconnectedUserIds] = useState({}); // { [userId]: true } when we receive student-disconnected; cleared on next heartbeat
  const [now, setNow] = useState(Date.now()); // ticks every 2s for live connection status
  const [, setForceUpdate] = useState(0);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [messageTarget, setMessageTarget] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [studentTranscripts, setStudentTranscripts] = useState({});
  const [transcriptModalOpen, setTranscriptModalOpen] = useState(false);
  const [transcriptStudent, setTranscriptStudent] = useState(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportStudent, setReportStudent] = useState(null);
  const [verifyCounts, setVerifyCounts] = useState({}); // { [userId]: { ok, fail } }
  const [snapshotUploads, setSnapshotUploads] = useState({}); // { [userId]: { success, fail, lastSuccess, lastFail } }
  const [systemTests, setSystemTests] = useState({}); // { [userId]: last results }
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileStudent, setProfileStudent] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [registrationByUser, setRegistrationByUser] = useState({}); // { [userId]: { registered, registeredAt? } }
  const [registrationLoading, setRegistrationLoading] = useState(false); // true while checking registration on load
  const [exclusionByUser, setExclusionByUser] = useState({}); // { [userId]: { excluded, excludedAt?, reason? } }
  const [exclusionLoading, setExclusionLoading] = useState(false);
  const [excludeDialogOpen, setExcludeDialogOpen] = useState(false);
  const [excludeTarget, setExcludeTarget] = useState(null);
  const [excludeReason, setExcludeReason] = useState('');
  const [excludeSubmitting, setExcludeSubmitting] = useState(false);
  const [violationsLoading, setViolationsLoading] = useState(false);
  const [violationStudentFilter, setViolationStudentFilter] = useState('');
  const [snapshotModalOpen, setSnapshotModalOpen] = useState(false);
  const [snapshotModalData, setSnapshotModalData] = useState(null);
  const [snapshotImgError, setSnapshotImgError] = useState(false);
  const [socketReady, setSocketReady] = useState(false);
  const [closedBookEnabled, setClosedBookEnabled] = useState(false);
  const [closedBookPolicy, setClosedBookPolicy] = useState(defaultClosedBookPolicy);
  const [closedBookSaving, setClosedBookSaving] = useState(false);
  const [securityByUser, setSecurityByUser] = useState({});
  const [securityEventsByUser, setSecurityEventsByUser] = useState({});
  const [securityDrawerOpen, setSecurityDrawerOpen] = useState(false);
  const [securityDrawerStudent, setSecurityDrawerStudent] = useState(null);
  const [liveExams, setLiveExams] = useState([]);
  const [examIdDraft, setExamIdDraft] = useState('');
  const violationsRef = useRef([]);
  const onViolationsUpdateRef = useRef(onViolationsUpdate);
  useEffect(() => { violationsRef.current = violations; }, [violations]);
  useEffect(() => { onViolationsUpdateRef.current = onViolationsUpdate; }, [onViolationsUpdate]);

  // Room id must match the widget examId. Do not default to 3131 — that hid live students on other exams.
  const query = new URLSearchParams(location.search);
  const urlLibraryId = (query.get('library_id') || '').trim();
  const library_id = urlLibraryId;
  const rollout_id = query.get('rollout_id') || '8699';
  const examRoomId = (library_id && String(library_id)) || '';
  const resolvedWsUrl = (() => {
    const runtimeUrl = (typeof window !== 'undefined' && window.VIRGIL_WS_URL) ? window.VIRGIL_WS_URL : null;
    const envUrl = process.env.REACT_APP_WEBSOCKET_URL || process.env.VIRGIL__WEBSOCKET_URL || null;
    const fallbackUrl = (typeof window !== 'undefined') ? window.location.origin : 'https://virgil.cpslearn.co.za';
    return runtimeUrl || envUrl || fallbackUrl;
  })();
  const UI_VERSION = APP_VERSION || '0.0.0';
  const versionLabel = `Admin ${UI_VERSION} (Widget ${WIDGET_VERSION || '?'}) · ${APP_DESCRIPTION || 'Proctoring admin'}`;

  // 1. Connect as admin even without library_id so we can discover live exams
  useEffect(() => {
    console.log('[Admin][WS] Query params', { library_id: urlLibraryId || null, rollout_id, defaulted: false });
    setSocketReady(false);
    console.log('[Admin][WS] Connecting as admin', { examId: examRoomId || null });
    websocketService.connect(examRoomId || null, null, 'admin');
    setSocketReady(true);
    setExamIdDraft(urlLibraryId);
  }, [examRoomId, urlLibraryId, rollout_id]);

  // 2. Subscribe to events AFTER socket is connected
  useEffect(() => {
    if (!websocketService.socket) return;

    // Listen for user connection status updates (assume backend emits { type: 'user_status', payload: { userId, connected } })
    const unsubscribeUserStatus = websocketService.subscribe('user_status', (data) => {
      console.log('[Admin][WS] user_status', data);
      setConnectedUsers(prev => ({ ...prev, [data.userId]: data.connected }));
    });

    const unsubscribeStudents = websocketService.subscribe('students', (data) => {
      console.log('[Admin][WS] students', data);
      setStudents(data);
    });

    const unsubscribeViolations = websocketService.subscribe('violations', (data) => {
      if (!data || typeof data !== 'object') return;
      const dedupeKey = violationEventDedupeKey(data);
      console.log('[Admin][WS] violations', data.clientViolationId || '(no cid)', data);
      const isDuplicate = dedupeKey && violationsRef.current.some((v) => violationEventDedupeKey(v) === dedupeKey);
      if (isDuplicate) return;
      const uid = data.userId ?? data.studentId ?? data.user_id;
      const normalized = normalizeViolation(data);
      setViolations(prev => mergeViolationLists(prev, [normalized]));
      if (uid) {
        setViolationCounts(prev => {
          const updated = { ...prev, [String(uid)]: (prev[String(uid)] || 0) + 1 };
          return updated;
        });
      }
      if (onViolationsUpdateRef.current) {
        onViolationsUpdateRef.current([...violationsRef.current, data]);
      }
      playAlertSound();
      if (window.Notification && Notification.permission === 'granted') {
        new Notification('Proctoring Violation', {
          body: `${data.type || 'Violation'}: ${data.details || ''}`.slice(0, 100),
          icon: '/favicon.ico',
        });
      }
    });

    // Also handle single 'violation' event (from backend broadcast)
    const unsubscribeViolation = websocketService.subscribe('violation', (data) => {
      if (!data || typeof data !== 'object') return;
      const dedupeKey = violationEventDedupeKey(data);
      console.log('[Admin][WS] violation', data.clientViolationId || '(no cid)', data);
      const isDuplicate = dedupeKey && violationsRef.current.some((v) => violationEventDedupeKey(v) === dedupeKey);
      if (isDuplicate) return;
      const uid = data.userId ?? data.studentId ?? data.user_id;
      const normalized = normalizeViolation(data);
      setViolations(prev => mergeViolationLists(prev, [normalized]));
      if (uid) {
        setViolationCounts(prev => {
          const updated = { ...prev, [String(uid)]: (prev[String(uid)] || 0) + 1 };
          return updated;
        });
      }
      if (onViolationsUpdateRef.current) {
        onViolationsUpdateRef.current([...violationsRef.current, data]);
      }
      playAlertSound();
      if (window.Notification && Notification.permission === 'granted') {
        new Notification('Proctoring Violation', {
          body: `${data.type || 'Violation'}: ${data.details || ''}`.slice(0, 100),
          icon: '/favicon.ico',
        });
      }
    });

    const unsubscribeFaceMismatch = websocketService.subscribe('face_mismatch', (data) => {
      const violation = {
        type: 'face_mismatch',
        studentId: 'current_student',
        timestamp: data.timestamp,
        severity: 'high',
        confidence: data.confidence
      };
      setViolations(prev => [...prev, violation]);
      if (onViolationsUpdateRef.current) {
        onViolationsUpdateRef.current([...violationsRef.current, violation]);
      }
    });

    // Listen for connected_users event to update connected users list
    const unsubscribeConnectedUsers = websocketService.subscribe('connected_users', (data) => {
      console.log('[Admin][WS] connected_users', data);
      const ids = (data && data.userIds ? data.userIds : []).map((id) => String(id));
      if (!ids.length && !(data && Array.isArray(data.userIds))) return;
      setConnectedUsers((prev) => {
        const updated = { ...prev };
        ids.forEach((userId) => { updated[userId] = true; });
        Object.keys(updated).forEach((userId) => {
          if (!ids.includes(userId)) updated[userId] = false;
        });
        return updated;
      });
      const nowTs = Date.now();
      setLastHeartbeats((prev) => {
        const updated = { ...prev };
        ids.forEach((userId) => { updated[userId] = nowTs; });
        return updated;
      });
      setDisconnectedUserIds((prev) => {
        const next = { ...prev };
        let changed = false;
        ids.forEach((userId) => {
          if (next[userId]) {
            delete next[userId];
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    });

    // Listen for heartbeat events (may include faceVerifyResult for Verify counts)
    const unsubscribeHeartbeat = websocketService.subscribe('heartbeat', (data) => {
      const uid = data && (data.userId != null ? data.userId : data.user_id);
      const id = uid != null ? String(uid) : null;
      console.log('[Admin][WS] RECV heartbeat', id, data && data.faceVerifyResult ? 'has faceVerifyResult' : '');
      if (id) {
        const timestamp = data.timestamp ? new Date(data.timestamp).getTime() : Date.now();
        setLastHeartbeats(prev => {
          const updated = { ...prev, [id]: timestamp };
          console.log('[Admin][State] Updated lastHeartbeats:', updated);
          console.log('[Admin][State] Time diff for', id, ':', Date.now() - timestamp, 'ms');
          return updated;
        });
        setConnectedUsers(prev => ({ ...prev, [id]: true }));
        setForceUpdate(f => f + 1);
      }
      // Piggyback: face verify result in heartbeat (same channel as heartbeat; same userId resolution as face_verify_result)
      if (data && data.faceVerifyResult && id) {
        const success = !!data.faceVerifyResult.success;
        console.log('[Admin][WS] RECV face_verify from heartbeat -> updating Verify counts', { userId: id, success });
        setVerifyCounts(prev => {
          const prevUser = prev[id] || { ok: 0, fail: 0 };
          return {
            ...prev,
            [id]: {
              ok: prevUser.ok + (success ? 1 : 0),
              fail: prevUser.fail + (success ? 0 : 1)
            }
          };
        });
      }
      // Clear disconnected flag when we get a heartbeat (user reconnected or event order)
      if (id) {
        setDisconnectedUserIds(prev => {
          if (!prev[id]) return prev;
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }
    });

    const unsubscribeVerify = websocketService.subscribe('face_verify_result', (data) => {
      console.log('[Admin][WS] face_verify_result HANDLER CALLED', { data, userId: data && (data.userId != null ? data.userId : data.user_id) });
      const uid = data && (data.userId != null ? data.userId : data.user_id);
      const id = uid != null ? String(uid) : null;
      console.log('[Admin][WS] face_verify_result received', { data, resolvedUserId: id });
      if (!data || id == null) return;
      const success = !!data.success;
      setVerifyCounts(prev => {
        const prevUser = prev[id] || { ok: 0, fail: 0 };
        const next = {
          ...prev,
          [id]: {
            ok: prevUser.ok + (success ? 1 : 0),
            fail: prevUser.fail + (success ? 0 : 1)
          }
        };
        console.log('[Admin][WS] verifyCounts updated for', id, next[id]);
        return next;
      });
      if (!success) {
        const examForViolation = data.examId != null ? String(data.examId) : library_id;
        const ts = data.timestamp ? new Date(data.timestamp) : new Date();
        const failViolation = normalizeViolation(buildFaceVerifyFailureViolation(id, examForViolation, data.confidence, ts));
        const dedupeKey = violationEventDedupeKey(failViolation);
        if (!dedupeKey || !violationsRef.current.some((v) => violationEventDedupeKey(v) === dedupeKey)) {
          setViolations((prev) => mergeViolationLists(prev, [failViolation]));
          setViolationCounts((prev) => ({
            ...prev,
            [id]: (prev[id] || 0) + 1
          }));
        }
      }
    });

    const unsubscribeSysTest = websocketService.subscribe('system_test_result', (data) => {
      console.log('[Admin][WS] RECV system_test_result', data && data.userId, data && data.results ? Object.keys(data.results) : '');
      if (!data || !data.userId) return;
      setSystemTests(prev => ({ ...prev, [String(data.userId)]: data.results }));
    });

    // Listen for snapshot upload status events
    const unsubscribeSnapshotStatus = websocketService.subscribe('snapshot_upload_status', (data) => {
      if (!data || !data.userId) return;
      const userIdStr = String(data.userId);
      console.log('[Admin][WS] snapshot_upload_status', data);
      
      setSnapshotUploads(prev => {
        const prevUser = prev[userIdStr] || { success: 0, fail: 0, lastSuccess: null, lastFail: null };
        const updated = {
          ...prev,
          [userIdStr]: {
            success: prevUser.success + (data.success ? 1 : 0),
            fail: prevUser.fail + (data.success ? 0 : 1),
            lastSuccess: data.success ? new Date(data.timestamp || Date.now()).toISOString() : prevUser.lastSuccess,
            lastFail: !data.success ? new Date(data.timestamp || Date.now()).toISOString() : prevUser.lastFail,
            lastError: !data.success ? (data.error || 'Unknown error') : prevUser.lastError
          }
        };
        console.log('[Admin][State] Updated snapshotUploads for', userIdStr, ':', updated[userIdStr]);
        return updated;
      });
    });

    // Listen for admin socket connection/disconnection
    const handleConnect = () => {
      setIsConnected(true);
      try { websocketService.sendMessage('request_live_exams'); } catch (_) { /* ignore */ }
    };
    const handleDisconnect = () => setIsConnected(false);
    websocketService.socket.on('connect', handleConnect);
    websocketService.socket.on('disconnect', handleDisconnect);
    setIsConnected(websocketService.socket.connected);
    if (websocketService.socket.connected) {
      try { websocketService.sendMessage('request_live_exams'); } catch (_) { /* ignore */ }
    }

    // Listen for webcam_frame events and update studentFrames
    const unsubscribeWebcamFrame = websocketService.subscribe('webcam_frame', (data) => {
      if (!data || typeof data !== 'object' || !data.userId) {
        console.warn('[Admin][WS] Invalid webcam_frame data:', data);
        return;
      }
      
      // Normalize userId to string for consistent lookup
      const userIdStr = String(data.userId);
      const frameData = data.frame;
      
      // Validate frame data
      if (!frameData || typeof frameData !== 'string') {
        console.warn('[Admin][WS] Invalid frame data for userId:', userIdStr);
        return;
      }
      
      // Check if frame is valid base64 data URL
      if (!frameData.startsWith('data:image/')) {
        console.warn('[Admin][WS] Frame data is not a valid data URL:', userIdStr, 'First 50 chars:', frameData.substring(0, 50));
        return;
      }
      
      // Check frame size - suspiciously small frames might be corrupted
      if (frameData.length < 2000) {
        console.warn('[Admin][WS] ⚠️ Frame is very small (', frameData.length, 'bytes). Expected at least 2KB for a valid image. Frame preview:', frameData.substring(0, 100));
        // Still try to use it, but log the warning
      }
      
      // Extract base64 data and check it
      const base64Match = frameData.match(/^data:image\/[^;]+;base64,(.+)$/);
      if (base64Match && base64Match[1]) {
        const base64Data = base64Match[1];
        if (base64Data.length < 1000) {
          console.warn('[Admin][WS] ⚠️ Base64 data is too short (', base64Data.length, 'chars). Image may be corrupted.');
        }
      } else {
        console.warn('[Admin][WS] ⚠️ Could not extract base64 data from frame URL');
      }
      
      // Log first frame and every 30th frame to avoid spam
      const frameCount = (window._adminFrameCount = window._adminFrameCount || {})[userIdStr] || 0;
      if (frameCount === 0 || frameCount % 30 === 0) {
        console.log(`[Admin][WS] 📹 Received webcam_frame #${frameCount} from userId: ${userIdStr}, examId: ${data.examId}, frameSize: ${frameData?.length}, valid: ${frameData.startsWith('data:image/')}`);
        console.log('[Admin][WS] Current studentFrames keys:', Object.keys(window._currentStudentFrames || {}));
      }
      window._adminFrameCount = { ...window._adminFrameCount, [userIdStr]: frameCount + 1 };
      
      setStudentFrames(prev => {
        const updated = {
          ...prev,
          [userIdStr]: frameData,
        };
        window._currentStudentFrames = updated; // Debug helper
        
        // Update frame key to force image re-render
        setFrameKeys(prevKeys => ({
          ...prevKeys,
          [userIdStr]: Date.now() + Math.random() // Unique key for each frame update
        }));
        
        console.log(`[Admin][WS] Updated studentFrames for userId: ${userIdStr}, total frames in state: ${Object.keys(updated).length}`);
        return updated;
      });
    });

    const unsubscribeScreenFrame = websocketService.subscribe('screen_frame', (data) => {
      if (!data || !data.userId || typeof data.frame !== 'string') return;
      const userIdStr = String(data.userId);
      setStudentScreenFrames((prev) => ({ ...prev, [userIdStr]: data.frame }));
      setFrameKeys((prevKeys) => ({
        ...prevKeys,
        [`screen-${userIdStr}`]: Date.now()
      }));
    });

    // Listen for connection-update from WebSocket server
    const unsubscribeConnectionUpdate = websocketService.subscribe('connection-update', (data) => {
      console.log('[Admin][WS] connection-update', data);
      // The WebSocket server sends activeStudents count, but we need actual user list
      // We'll update based on heartbeats instead
    });

    // Listen for student-connected events
    const unsubscribeStudentConnected = websocketService.subscribe('student-connected', (data) => {
      console.log('[Admin][WS] student-connected', data);
      if (data && data.userId) {
        const uid = String(data.userId);
        setConnectedUsers(prev => ({ ...prev, [uid]: true }));
        setLastHeartbeats(prev => ({ ...prev, [uid]: Date.now() }));
        setDisconnectedUserIds(prev => {
          if (!prev[uid]) return prev;
          const next = { ...prev };
          delete next[uid];
          return next;
        });
      }
    });

    // Listen for student-disconnected events
    const unsubscribeStudentDisconnected = websocketService.subscribe('student-disconnected', (data) => {
      console.log('[Admin][WS] student-disconnected', data);
      if (data && data.userId != null) {
        const uid = String(data.userId);
        setConnectedUsers(prev => ({ ...prev, [uid]: false }));
        setLastHeartbeats(prev => ({ ...prev, [uid]: 0 }));
        setDisconnectedUserIds(prev => ({ ...prev, [uid]: true }));
      }
    });

    const unsubscribeRegistrationUpdated = websocketService.subscribe('registration_updated', (data) => {
      if (!data || data.userId == null) return;
      const uid = String(data.userId);
      console.log('[Admin][WS] registration_updated', data);
      setRegistrationByUser((prev) => ({
        ...prev,
        [uid]: {
          registered: true,
          registeredAt: data.registeredAt || new Date().toISOString()
        }
      }));
    });

    const unsubscribeExamSecurity = websocketService.subscribe('exam-security', (data) => {
      const cid = String(data.candidateId || (data.event && data.event.candidateId) || '');
      if (!cid) return;
      if (data.securityStatus) {
        setSecurityByUser((prev) => ({ ...prev, [cid]: { ...(prev[cid] || {}), ...data.securityStatus } }));
      }
      const ev = data.event;
      if (ev && ev.eventId) {
        setSecurityEventsByUser((prev) => {
          const list = prev[cid] || [];
          if (list.some((e) => e.eventId === ev.eventId)) return prev;
          return { ...prev, [cid]: [{ ...ev, timestamp: ev.timestamp || new Date().toISOString() }, ...list].slice(0, 200) };
        });
      }
    });
    const unsubscribeSecurityStatus = websocketService.subscribe('security_status', (data) => {
      const cid = String(data.candidateId || '');
      if (!cid) return;
      setSecurityByUser((prev) => ({ ...prev, [cid]: { ...(prev[cid] || {}), ...data } }));
    });

    const unsubscribeLiveExams = websocketService.subscribe('live_exams', (data) => {
      const exams = data && Array.isArray(data.exams) ? data.exams : [];
      console.log('[Admin][WS] live_exams', exams);
      setLiveExams(exams);
    });

    const unsubscribeExclusionUpdated = websocketService.subscribe('exclusion_updated', (data) => {
      if (!data || data.userId == null) return;
      const uid = String(data.userId);
      console.log('[Admin][WS] exclusion_updated', data);
      setExclusionByUser((prev) => ({
        ...prev,
        [uid]: data.excluded
          ? {
              excluded: true,
              excludedAt: data.excludedAt || new Date().toISOString(),
              excludedBy: data.excludedBy,
              reason: data.reason
            }
          : { excluded: false }
      }));
    });

    // Extra: log rollout_id and all WebSocket events (including face_verify_result for debugging)
    const unsubscribeAll = websocketService.subscribe('*', (type, data) => {
      if (type === 'face_verify_result') {
        console.log('[Admin][WS] * face_verify_result', type, data);
      }
    });

    return () => {
      unsubscribeUserStatus();
      unsubscribeStudents();
      unsubscribeViolations();
      unsubscribeViolation();
      unsubscribeFaceMismatch();
      unsubscribeConnectedUsers();
      unsubscribeHeartbeat();
      unsubscribeVerify();
      unsubscribeSysTest();
      unsubscribeSnapshotStatus();
      unsubscribeWebcamFrame();
      unsubscribeScreenFrame();
      unsubscribeConnectionUpdate();
      unsubscribeStudentConnected();
      unsubscribeStudentDisconnected();
      unsubscribeRegistrationUpdated();
      unsubscribeExclusionUpdated();
      unsubscribeExamSecurity();
      unsubscribeSecurityStatus();
      unsubscribeLiveExams();
      unsubscribeAll();
      websocketService.socket.off('connect', handleConnect);
      websocketService.socket.off('disconnect', handleDisconnect);
    };
  }, [websocketService.socket, socketReady]);

  useEffect(() => {
    let cancelled = false;
    const pull = async () => {
      try {
        const res = await fetch('/api/live-exams');
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && Array.isArray(data.exams)) setLiveExams(data.exams);
      } catch (_) { /* ignore */ }
    };
    pull();
    const id = setInterval(pull, 5000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const goToExam = useCallback((examId) => {
    const nextId = String(examId || '').trim();
    if (!nextId) return;
    const next = new URLSearchParams(location.search);
    next.set('library_id', nextId);
    if (!next.get('rollout_id')) next.set('rollout_id', rollout_id);
    navigate(`/admin?${next.toString()}`);
  }, [location.search, navigate, rollout_id]);

  useEffect(() => {
    if (urlLibraryId || !liveExams.length) return;
    const best = liveExams[0];
    if (!best || best.examId == null) return;
    console.log('[Admin] No library_id in URL — following live exam', best);
    goToExam(best.examId);
  }, [urlLibraryId, liveExams, goToExam]);

  const otherLiveExams = useMemo(
    () => (liveExams || []).filter((e) => String(e.examId) !== String(library_id || '')),
    [liveExams, library_id]
  );

  useEffect(() => {
    const fetchStudents = async () => {
      if (!library_id || !rollout_id) return;
      setLoading(true);
      setError(null);
      try {
        const url = `/api/external/getExamLearners?library_id=${encodeURIComponent(library_id)}&rollout_id=${encodeURIComponent(rollout_id)}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error('Failed to fetch student list');
        const data = await res.json();
        const list = Array.isArray(data.userList) ? data.userList : Array.isArray(data.learners) ? data.learners : [];
        if (data.status === 'success' && list.length >= 0) {
          setStudents(list);
        } else if (list.length > 0) {
          setStudents(list);
        } else {
          setError(data.message || 'Invalid response from server');
        }
      } catch (err) {
        setError(err.message || 'Failed to fetch student list');
      } finally {
        setLoading(false);
      }
    };
    fetchStudents();
  }, [library_id, rollout_id]);

  // Fetch registration status for all students when list is loaded (on load and when students change)
  const fetchRegistrationStatus = useCallback(async () => {
    if (!library_id || !students.length) {
      setRegistrationByUser({});
      setRegistrationLoading(false);
      return;
    }
    setRegistrationLoading(true);
    try {
      const userIds = students.map((s) => s.user_id ?? s.id ?? s.learner_id).filter((id) => id != null && id !== '');
      if (userIds.length === 0) {
        setRegistrationByUser({});
        setRegistrationLoading(false);
        return;
      }
      const payload = { examId: library_id, userIds };
      console.log('[Admin] Fetching registration status', { examId: library_id, userIds, count: userIds.length });
      const res = await fetch('/api/admin/registration-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        console.warn('[Admin] registration-status not ok', res.status);
        setRegistrationLoading(false);
        return;
      }
      const data = await res.json();
      console.log('[Admin] Registration status response', { keys: Object.keys(data), registered: Object.entries(data).filter(([, v]) => v && v.registered).map(([k]) => k) });
      setRegistrationByUser(data);
    } catch (err) {
      console.error('[Admin] Failed to fetch registration status', err);
    } finally {
      setRegistrationLoading(false);
    }
  }, [library_id, students]);

  const fetchExclusionStatus = useCallback(async () => {
    if (!library_id || !students.length) {
      setExclusionByUser({});
      setExclusionLoading(false);
      return;
    }
    setExclusionLoading(true);
    try {
      const userIds = students.map((s) => s.user_id ?? s.id ?? s.learner_id).filter((id) => id != null && id !== '');
      if (userIds.length === 0) {
        setExclusionByUser({});
        setExclusionLoading(false);
        return;
      }
      const res = await fetch('/api/admin/exclusion-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ examId: library_id, userIds })
      });
      if (!res.ok) {
        console.warn('[Admin] exclusion-status not ok', res.status);
        setExclusionLoading(false);
        return;
      }
      const data = await res.json();
      setExclusionByUser(data);
    } catch (err) {
      console.error('[Admin] Failed to fetch exclusion status', err);
    } finally {
      setExclusionLoading(false);
    }
  }, [library_id, students]);

  useEffect(() => {
    if (!library_id || !students.length) return;
    fetchExclusionStatus();
    const poll = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        fetchExclusionStatus();
      }
    }, 15000);
    return () => clearInterval(poll);
  }, [library_id, students, fetchExclusionStatus]);

  useEffect(() => {
    if (!library_id || !students.length) return;
    fetchRegistrationStatus();
    const poll = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        fetchRegistrationStatus();
      }
    }, 15000);
    return () => clearInterval(poll);
  }, [library_id, students, fetchRegistrationStatus]);

  const handleStopMonitoring = (studentId) => {
    websocketService.sendMessage('stop_monitoring', { studentId });
  };

  const handleOpenExcludeDialog = (student) => {
    setExcludeTarget(student);
    setExcludeReason('');
    setExcludeDialogOpen(true);
  };

  const handleCloseExcludeDialog = () => {
    if (excludeSubmitting) return;
    setExcludeDialogOpen(false);
    setExcludeTarget(null);
    setExcludeReason('');
  };

  const handleConfirmExclude = async () => {
    if (!excludeTarget || !library_id) return;
    const userId = excludeTarget.user_id ?? excludeTarget.id ?? excludeTarget.learner_id;
    if (userId == null) return;
    setExcludeSubmitting(true);
    try {
      const res = await fetch('/api/admin/proctoring-exclude', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          examId: library_id,
          userId,
          excludedBy: 'admin',
          reason: excludeReason.trim() || undefined
        })
      });
      if (!res.ok) throw new Error('Failed to exclude user');
      const data = await res.json();
      const sid = String(userId);
      setExclusionByUser((prev) => ({
        ...prev,
        [sid]: {
          excluded: true,
          excludedAt: data.excludedAt || new Date().toISOString(),
          excludedBy: data.excludedBy,
          reason: data.reason
        }
      }));
      handleCloseExcludeDialog();
    } catch (err) {
      console.error('[Admin] Exclude user failed', err);
      alert('Failed to exclude user from monitoring. Please try again.');
    } finally {
      setExcludeSubmitting(false);
    }
  };

  const handleIncludeUser = async (student) => {
    const userId = student.user_id ?? student.id ?? student.learner_id;
    if (userId == null || !library_id) return;
    const sid = String(userId);
    try {
      const res = await fetch('/api/admin/proctoring-include', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ examId: library_id, userId })
      });
      if (!res.ok) throw new Error('Failed to re-include user');
      setExclusionByUser((prev) => ({ ...prev, [sid]: { excluded: false } }));
    } catch (err) {
      console.error('[Admin] Re-include user failed', err);
      alert('Failed to re-enable monitoring for this user. Please try again.');
    }
  };

  const handleSendAlert = (studentId, message) => {
    websocketService.sendMessage('send_alert', { studentId, message });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    if (!library_id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [polRes, stRes, evRes] = await Promise.all([
          fetch(`/api/exams/${encodeURIComponent(library_id)}/security-policy`),
          fetch(`/api/exams/${encodeURIComponent(library_id)}/security-status`),
          fetch(`/api/exams/${encodeURIComponent(library_id)}/security-events?limit=300`)
        ]);
        const pol = await polRes.json().catch(() => ({}));
        const st = await stRes.json().catch(() => ({}));
        const ev = await evRes.json().catch(() => ({}));
        if (cancelled) return;
        setClosedBookEnabled(!!pol.examEnabled);
        setClosedBookPolicy(mergePolicy(pol.policy));
        const byUser = {};
        (st.candidates || []).forEach((c) => {
          byUser[String(c.candidateId)] = c;
        });
        setSecurityByUser(byUser);
        const grouped = {};
        (ev.events || []).forEach((e) => {
          const cid = String(e.candidateId);
          if (!grouped[cid]) grouped[cid] = [];
          grouped[cid].push(e);
        });
        setSecurityEventsByUser(grouped);
      } catch (err) {
        console.warn('[Admin] closed-book security hydrate failed', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [library_id]);

  const saveClosedBookPolicy = async (nextEnabled, nextPolicy) => {
    setClosedBookSaving(true);
    try {
      const res = await fetch(`/api/exams/${encodeURIComponent(library_id)}/security-policy`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: nextEnabled, policy: nextPolicy })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json.error || `Failed to save closed-book policy (${res.status})`);
      }
      setClosedBookEnabled(!!json.examEnabled);
      setClosedBookPolicy(mergePolicy(json.policy));
    } catch (err) {
      console.error('[Admin] save closed-book policy', err);
      alert(err.message || 'Failed to save Closed Book setting');
    } finally {
      setClosedBookSaving(false);
    }
  };

  const handleClearSessionAudit = () => {
    setViolations([]);
    violationsRef.current = [];
    setViolationCounts({});
    setVerifyCounts({});
    setSnapshotUploads({});
    setStudentTranscripts({});
    if (onViolationsUpdateRef.current) onViolationsUpdateRef.current([]);
  };

  const auditFilenameBase = `proctoring-audit-${library_id || 'exam'}`;

  const studentNameById = useMemo(() => {
    const m = {};
    (students || []).forEach((s) => {
      const id = String(s.user_id ?? s.id ?? s.learner_id ?? '');
      if (id) m[id] = `${s.first_name || ''} ${s.last_name || ''}`.trim();
    });
    return m;
  }, [students]);

  const filteredViolations = useMemo(() => {
    const q = violationStudentFilter.trim().toLowerCase();
    if (!q) return violations;
    return violations.filter((v) => {
      const uid = String(v.userId ?? v.user_id ?? '');
      const name = (studentNameById[uid] || '').toLowerCase();
      return uid.includes(q) || name.includes(q) || (v.type || '').toLowerCase().includes(q);
    });
  }, [violations, violationStudentFilter, studentNameById]);

  const violationsGroupedByStudent = useMemo(() => {
    const groups = {};
    filteredViolations.forEach((v) => {
      const uid = String(v.userId ?? v.user_id ?? '');
      if (!uid) return;
      if (!groups[uid]) groups[uid] = [];
      groups[uid].push(v);
    });
    Object.keys(groups).forEach((uid) => {
      groups[uid] = sortViolationsNewestFirst(groups[uid]);
    });
    return groups;
  }, [filteredViolations]);

  const studentViolationRows = useMemo(() => {
    const ids = new Set([
      ...(students || []).map((s) => String(s.user_id ?? s.id ?? s.learner_id ?? '')).filter(Boolean),
      ...Object.keys(violationsGroupedByStudent)
    ]);
    const q = violationStudentFilter.trim().toLowerCase();
    const rows = [...ids].map((uid) => {
      const student = (students || []).find(
        (s) => String(s.user_id ?? s.id ?? s.learner_id ?? '') === uid
      );
      const name = student
        ? `${student.first_name || ''} ${student.last_name || ''}`.trim()
        : (studentNameById[uid] || '');
      const list = violationsGroupedByStudent[uid] || [];
      return {
        userId: uid,
        name: name || `Student ${uid}`,
        email: student?.email || '',
        count: list.length,
        violations: list,
        student
      };
    });
    const filtered = q
      ? rows.filter(
          (r) =>
            r.userId.includes(q) ||
            r.name.toLowerCase().includes(q) ||
            r.email.toLowerCase().includes(q) ||
            r.violations.some((v) => (v.type || '').toLowerCase().includes(q))
        )
      : rows;
    return filtered.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [students, violationsGroupedByStudent, studentNameById, violationStudentFilter]);

  const violationSummary = useMemo(() => {
    const total = filteredViolations.length;
    const withViolations = studentViolationRows.filter((r) => r.count > 0).length;
    return { total, withViolations, learners: studentViolationRows.length };
  }, [filteredViolations, studentViolationRows]);

  useEffect(() => {
    const liveIds = Object.keys(lastHeartbeats).filter((id) =>
      isStudentConnected(id, lastHeartbeats, disconnectedUserIds)
    );
    if (!liveIds.length) return;
    setStudents((prev) => {
      const have = new Set((prev || []).map((s) => String(s.user_id ?? s.id ?? s.learner_id ?? '')));
      const extras = liveIds
        .filter((id) => id && !have.has(id))
        .map((id) => ({
          user_id: id,
          first_name: 'Student',
          last_name: id,
          email: ''
        }));
      return extras.length ? [...(prev || []), ...extras] : prev;
    });
  }, [lastHeartbeats, disconnectedUserIds]);

  const studentStatusSummary = useMemo(() => {
    let registered = 0;
    let connected = 0;
    let excluded = 0;
    students.forEach((student) => {
      const sid = String(student.user_id ?? student.id ?? student.learner_id ?? '');
      if (!sid) return;
      if (registrationByUser[sid]?.registered) registered += 1;
      if (exclusionByUser[sid]?.excluded) excluded += 1;
      if (isStudentConnected(sid, lastHeartbeats, disconnectedUserIds, now)) connected += 1;
    });
    return { total: students.length, registered, connected, excluded };
  }, [students, registrationByUser, exclusionByUser, lastHeartbeats, disconnectedUserIds, now]);

  const handleOpenSnapshotModal = (violation, studentLabel = '') => {
    const url = violation?.snapshot_url ?? violation?.snapshotUrl;
    if (!url) return;
    setSnapshotModalData({
      url,
      type: violation.type || 'Violation',
      timestamp: violation.timestamp,
      details: violation.details || '',
      studentLabel
    });
    setSnapshotImgError(false);
    setSnapshotModalOpen(true);
  };

  const handleCloseSnapshotModal = () => {
    setSnapshotModalOpen(false);
    setSnapshotModalData(null);
    setSnapshotImgError(false);
  };

  const renderStudentViolationsTable = (list, { limit, studentLabel } = {}) => {
    const rows = limit ? list.slice(0, limit) : list;
    return (
    <TableContainer sx={{ maxHeight: limit ? 240 : 420 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Time</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Severity</TableCell>
            <TableCell>Details</TableCell>
            <TableCell>Snapshot</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5}>No violations for this student.</TableCell>
            </TableRow>
          ) : (
            rows.map((v, idx) => {
              const sev = (v.severity || 'medium').toLowerCase();
              const sevColor = sev === 'high' ? 'error' : sev === 'medium' ? 'warning' : 'default';
              return (
                <TableRow key={v.id || violationEventDedupeKey(v) || idx} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatViolationTime(v.timestamp)}</TableCell>
                  <TableCell>{v.type}</TableCell>
                  <TableCell>
                    <Chip size="small" label={v.severity || 'medium'} color={sevColor} variant="outlined" />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>{v.details || '—'}</TableCell>
                  <TableCell>
                    {v.snapshot_url ? (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => handleOpenSnapshotModal(v, studentLabel)}
                      >
                        View
                      </Button>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
    );
  };

  // Handle opening/closing the video feed modal
  useEffect(() => {
    if (viewModalOpen && selectedStudent) {
      // Request student to start sending webcam frames
      websocketService.sendMessage('start_webcam_feed', {
        userId: selectedStudent.user_id,
        examId: library_id,
      });
    } else if (!viewModalOpen && selectedStudent) {
      // Request student to stop sending webcam frames
      websocketService.sendMessage('stop_webcam_feed', {
        userId: selectedStudent.user_id,
        examId: library_id,
      });
    }
  }, [viewModalOpen, selectedStudent, library_id]);

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 2000);
    return () => clearInterval(interval);
  }, []);

  const handleOpenMessageDialog = (studentId) => {
    setMessageTarget(String(studentId));
    setMessageDialogOpen(true);
    setMessageText('');
  };

  const handleCloseMessageDialog = () => {
    setMessageDialogOpen(false);
    setMessageTarget(null);
    setMessageText('');
  };

  const handleSendMessage = () => {
    if (!messageTarget || !messageText.trim()) return;
    const uid = String(messageTarget);
    const payload = { userId: uid, message: messageText.trim(), examId: String(library_id) };
    websocketService.sendMessage('send_message', payload);
    console.log('[Admin][WS] send_message ->', payload);
    handleCloseMessageDialog();
  };

  const fetchViolationsFromApi = useCallback(async () => {
    if (!library_id) return;
    setViolationsLoading(true);
    try {
      const res = await fetch(`/api/violations?examId=${encodeURIComponent(library_id)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const violationsData = await res.json();
      if (Array.isArray(violationsData)) {
        const normalized = violationsData.map(normalizeViolation).filter(Boolean);
        setViolations((prev) => mergeViolationLists(prev, normalized));
        const counts = {};
        mergeViolationLists([], [...normalized, ...violationsRef.current]).forEach((v) => {
          const uid = String(v.userId ?? v.user_id ?? '');
          if (uid) counts[uid] = (counts[uid] || 0) + 1;
        });
        setViolationCounts((prev) => ({ ...prev, ...counts }));
      }
    } catch (err) {
      console.log('[Admin] Error fetching violations:', err);
    } finally {
      setViolationsLoading(false);
    }
  }, [library_id]);

  // Fetch all violations and transcripts for the current exam on mount
  useEffect(() => {
    console.log('[Admin] Fetching violations and transcripts', { library_id });
    const fetchAll = async () => {
      try {
        let violationsUrl = '/api/violations';
        let transcriptsUrl = '/api/transcripts';
        if (library_id) {
          violationsUrl += `?examId=${library_id}`;
          transcriptsUrl += `?examId=${library_id}`;
        }
        const [violationsRes, transcriptsRes] = await Promise.all([
          fetch(violationsUrl),
          fetch(transcriptsUrl)
        ]);
        const violationsData = await violationsRes.json();
        const transcriptsData = await transcriptsRes.json();
        console.log('[Admin] Violations fetched:', violationsData);
        console.log('[Admin] Transcripts fetched:', transcriptsData);
        if (Array.isArray(violationsData)) {
          const normalized = violationsData.map(normalizeViolation).filter(Boolean);
          setViolations(normalized);
          const counts = {};
          normalized.forEach((v) => {
            const uid = String(v.userId ?? v.user_id);
            if (uid) counts[uid] = (counts[uid] || 0) + 1;
          });
          setViolationCounts(prev => ({ ...prev, ...counts }));
        }
        if (transcriptsData && typeof transcriptsData === 'object') setStudentTranscripts(transcriptsData);
      } catch (err) {
        console.log('[Admin] Error fetching violations/transcripts:', err);
      }
    };
    fetchAll();
  }, [library_id]);

  // Listen for transcript events
  useEffect(() => {
    if (!websocketService.socket) return;
    const unsubscribeTranscript = websocketService.subscribe('transcript', (data) => {
      if (!data || !data.userId) return;
      const text = data.transcript || data.text;
      if (!text) return;
      setStudentTranscripts(prev => {
        const existing = prev[data.userId];
        const newEntry = { transcript: text, timestamp: (data.timestamp && data.timestamp.toISOString ? data.timestamp.toISOString() : new Date().toISOString()) };
        const arr = Array.isArray(existing) ? [...existing, newEntry] : (existing ? [{ transcript: String(existing), timestamp: new Date().toISOString() }, newEntry] : [newEntry]);
        return { ...prev, [data.userId]: arr };
      });
    });
    return () => unsubscribeTranscript();
  }, [websocketService.socket]);

  const handleOpenTranscriptModal = (student) => {
    setTranscriptStudent(student);
    setTranscriptModalOpen(true);
  };
  const handleCloseTranscriptModal = () => {
    setTranscriptModalOpen(false);
    setTranscriptStudent(null);
  };

  const handleOpenReportModal = (student) => {
    setReportStudent(student);
    setReportModalOpen(true);
  };
  const handleCloseReportModal = () => {
    setReportModalOpen(false);
    setReportStudent(null);
  };

  const handleOpenProfileModal = async (student) => {
    setProfileStudent(student);
    setProfileModalOpen(true);
    setProfileLoading(true);
    setProfileData(null);
    const studentId = String(student.user_id ?? student.id ?? student.learner_id ?? '');
    if (!studentId) {
      setProfileLoading(false);
      return;
    }
    try {
      // Fetch registration data (use same examId as list: library_id)
      const [regRes, idRes] = await Promise.all([
        fetch(`/api/check-registration`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: studentId, examId: library_id })
        }),
        fetch(`/api/user-profile/${encodeURIComponent(studentId)}?examId=${encodeURIComponent(library_id)}`)
      ]);
      
      const regData = await regRes.json();
      const idData = idRes.ok ? await idRes.json() : null;
      
      setProfileData({
        registration: regData,
        idDocument: idData
      });
      // Keep list in sync: if profile shows registered, update registrationByUser so the row shows green
      if (regData && typeof regData.registered === 'boolean') {
        setRegistrationByUser((prev) => ({
          ...prev,
          [studentId]: { registered: regData.registered, registeredAt: regData.registeredAt }
        }));
      }
    } catch (err) {
      console.error('[Admin] Profile fetch error:', err);
    } finally {
      setProfileLoading(false);
    }
  };
  
  const handleCloseProfileModal = () => {
    setProfileModalOpen(false);
    setProfileStudent(null);
    setProfileData(null);
  };

  // Alert sound for violations
  const playAlertSound = () => {
    const audio = new window.Audio('/alert.mp3');
    audio.play().catch((err) => {
      // Suppress autoplay error
    });
  };

  // Request notification permission on load
  if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission !== 'granted') {
    Notification.requestPermission();
  }

  return (
    <Box component="main" sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
          Admin Dashboard
        </Typography>
      <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
        {versionLabel} | Room: {examRoomId ? `exam_${examRoomId}` : 'n/a'} | WS: {resolvedWsUrl}
      </Typography>
      <Chip
        label={isConnected ? 'Connected' : 'Disconnected'}
        color={isConnected ? 'success' : 'error'}
        sx={{ mb: 2, mr: 1 }}
      />
      {!urlLibraryId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No exam ID in the URL. Admin used to silently watch exam 3131, so students on other exams never appeared as Connected.
          {liveExams.length
            ? ` Opening live exam ${liveExams[0].examId}…`
            : ' Waiting for a student heartbeat, or enter the widget exam ID below.'}
        </Alert>
      )}
      {urlLibraryId && otherLiveExams.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Watching exam {urlLibraryId}. Live students are on a different exam:{' '}
          {otherLiveExams.map((exam) => (
            <Button key={exam.examId} size="small" variant="contained" sx={{ ml: 1, mt: 0.5 }} onClick={() => goToExam(exam.examId)}>
              Open exam {exam.examId} ({exam.count} live)
            </Button>
          ))}
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label="Exam ID"
          value={examIdDraft}
          onChange={(e) => setExamIdDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') goToExam(examIdDraft); }}
          placeholder="e.g. 6963"
        />
        <Button variant="contained" onClick={() => goToExam(examIdDraft)} disabled={!String(examIdDraft || '').trim()}>
          Watch exam
        </Button>
      </Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: closedBookEnabled ? 'primary.main' : 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <SecurityIcon color={closedBookEnabled ? 'primary' : 'action'} />
          <Box sx={{ flex: '1 1 240px' }}>
            <Typography variant="subtitle1" fontWeight={600}>Closed Book Security</Typography>
            <Typography variant="body2" color="text.secondary">
              {library_id
                ? `Turn on browser lockdown for exam ${library_id} (fullscreen, screen share, copy/paste). Off by default.`
                : 'Open admin with ?library_id=… to enable closed book for an exam.'}
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={closedBookEnabled}
                disabled={closedBookSaving || !library_id}
                onChange={(e) => saveClosedBookPolicy(e.target.checked, closedBookPolicy)}
              />
            }
            label={closedBookEnabled ? 'On' : 'Off'}
          />
        </Box>
        {closedBookEnabled && (
          <FormGroup row sx={{ mt: 1 }}>
            {[
              ['requireFullscreen', 'Fullscreen'],
              ['requireScreenShare', 'Screen share'],
              ['blockCopy', 'Block copy'],
              ['blockPaste', 'Block paste'],
              ['blockPrint', 'Block print'],
              ['blockExternalLinks', 'Block links'],
              ['blockTextSelection', 'Block selection']
            ].map(([key, label]) => (
              <FormControlLabel
                key={key}
                control={
                  <Switch
                    size="small"
                    checked={!!closedBookPolicy[key]}
                    disabled={closedBookSaving}
                    onChange={(e) => {
                      const next = { ...closedBookPolicy, [key]: e.target.checked };
                      setClosedBookPolicy(next);
                      saveClosedBookPolicy(true, next);
                    }}
                  />
                }
                label={label}
              />
            ))}
          </FormGroup>
        )}
      </Paper>
      <Button
        size="small"
        variant="outlined"
        onClick={() => fetchRegistrationStatus()}
        disabled={registrationLoading || !students.length}
        sx={{ mb: 2 }}
      >
        {registrationLoading ? 'Checking...' : 'Refresh registration status'}
      </Button>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Paper
          variant="outlined"
          sx={{
            flex: '1 1 140px',
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            minWidth: 140
          }}
        >
          <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
            <GroupsIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" component="div" lineHeight={1.2}>
              {loading ? '…' : studentStatusSummary.total}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Students
            </Typography>
          </Box>
        </Paper>
        <Paper
          variant="outlined"
          sx={{
            flex: '1 1 140px',
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            minWidth: 140,
            borderColor: studentStatusSummary.registered > 0 ? 'success.light' : undefined
          }}
        >
          <Avatar sx={{ bgcolor: 'success.main', width: 40, height: 40 }}>
            <HowToRegIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" component="div" lineHeight={1.2}>
              {registrationLoading ? '…' : studentStatusSummary.registered}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Registered
            </Typography>
          </Box>
        </Paper>
        <Paper
          variant="outlined"
          sx={{
            flex: '1 1 140px',
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            minWidth: 140,
            borderColor: studentStatusSummary.connected > 0 ? 'success.light' : undefined
          }}
        >
          <Avatar sx={{ bgcolor: studentStatusSummary.connected > 0 ? 'success.main' : 'grey.500', width: 40, height: 40 }}>
            <WifiIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" component="div" lineHeight={1.2}>
              {studentStatusSummary.connected}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Connected
            </Typography>
          </Box>
        </Paper>
        <Paper
          variant="outlined"
          sx={{
            flex: '1 1 140px',
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            minWidth: 140,
            borderColor: studentStatusSummary.excluded > 0 ? 'warning.light' : undefined
          }}
        >
          <Avatar sx={{ bgcolor: studentStatusSummary.excluded > 0 ? 'warning.main' : 'grey.500', width: 40, height: 40 }}>
            <BlockIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" component="div" lineHeight={1.2}>
              {exclusionLoading ? '…' : studentStatusSummary.excluded}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Excluded
            </Typography>
          </Box>
        </Paper>
      </Box>
      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="Active Students" />
        <Tab
          icon={
            <Badge badgeContent={violationSummary.withViolations} color="error" max={999}>
              <GavelIcon />
            </Badge>
          }
          iconPosition="start"
          label="Violations"
        />
        <Tab label="Audit" icon={<AssessmentIcon />} iconPosition="start" />
      </Tabs>
      {activeTab === 0 && (
        <Paper>
          {loading && <CircularProgress />}
          {error && <Typography color="error">{error}</Typography>}
          {!loading && !error && (
        <List>
              {students.map((student) => {
                const sid = String(student.user_id ?? student.id ?? student.learner_id ?? '');
                const isExcluded = !!exclusionByUser[sid]?.excluded;
                return (
                <Fragment key={sid}>
                  <ListItem
                    sx={{
                      borderLeft: isExcluded ? '4px solid #ed6c02' : (registrationByUser[sid]?.registered ? '4px solid #2e7d32' : '4px solid transparent'),
                      backgroundColor: isExcluded ? 'rgba(237, 108, 2, 0.08)' : (registrationByUser[sid]?.registered ? 'rgba(46, 125, 50, 0.06)' : undefined),
                      opacity: isExcluded ? 0.85 : 1,
                    }}
                  >
                    {/* Registration indicator: green avatar first in row so status is obvious */}
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        mr: 2,
                        flexShrink: 0,
                        bgcolor: registrationByUser[sid]?.registered ? 'success.main' : (registrationLoading ? 'grey.400' : 'grey.500'),
                        color: 'white',
                      }}
                      title={registrationByUser[sid]?.registered ? 'Registered for proctoring' : (registrationLoading ? 'Checking...' : 'Not registered')}
                    >
                      {registrationByUser[sid]?.registered ? <HowToRegIcon /> : <PersonIcon />}
                    </Avatar>
                <ListItemText
                      primary={
                        <>
                          {`${student.first_name} ${student.last_name}`}
                          {registrationByUser[sid]?.registered && !isExcluded && (
                            <Typography component="span" sx={{ color: 'success.main', fontWeight: 600, ml: 1 }}>✓ Registered</Typography>
                          )}
                          {isExcluded && (
                            <Typography component="span" sx={{ color: 'warning.main', fontWeight: 600, ml: 1 }}>Excluded from monitoring</Typography>
                          )}
                        </>
                      }
                  secondary={
                        <>
                          {`${student.email} (ID: ${sid})`}
                          <br />
                          <span style={{
                            color: isStudentConnected(sid, lastHeartbeats, disconnectedUserIds) ? 'green' : 'gray',
                            fontWeight: 'bold',
                            marginRight: 8
                          }}>
                            {isStudentConnected(sid, lastHeartbeats, disconnectedUserIds) ? 'Connected' : 'Disconnected'}
                          </span>
                          <span style={{ marginLeft: 8 }}>
                            Violations: <b>{violationCounts[sid] || 0}</b>
                          </span>
                          {closedBookEnabled && (
                            <span style={{ marginLeft: 8 }}>
                              Security: <b>{securityByUser[sid]?.securityStatus || '—'}</b>
                              {' '}Risk: <b>{securityByUser[sid]?.riskScore ?? 0}</b>
                            </span>
                          )}
                          <span style={{ marginLeft: 8 }}>
                            {exclusionLoading ? (
                              <span style={{ color: '#888' }}>Checking exclusion…</span>
                            ) : isExcluded ? (
                              <span style={{ color: '#ed6c02', fontWeight: 'bold' }}>Monitoring disabled</span>
                            ) : registrationLoading ? (
                              <span style={{ color: '#888' }}>Checking...</span>
                            ) : registrationByUser[sid]?.registered ? (
                              <span style={{ color: '#2e7d32', fontWeight: 'bold' }}>Registered</span>
                            ) : (
                              <span style={{ color: '#666' }}>Not registered</span>
                            )}
                          </span>
                        </>
                  }
                />
                {closedBookEnabled && (
                  <>
                    <Chip
                      size="small"
                      icon={<SecurityIcon sx={{ fontSize: 16 }} />}
                      label={securityByUser[sid]?.securityStatus || 'UNKNOWN'}
                      color={
                        securityByUser[sid]?.securityStatus === 'SECURE'
                          ? 'success'
                          : securityByUser[sid]?.securityStatus === 'WARNING'
                            ? 'warning'
                            : securityByUser[sid]?.securityStatus === 'NON_COMPLIANT'
                              ? 'error'
                              : 'default'
                      }
                      sx={{ mx: 0.5, flexShrink: 0 }}
                    />
                    <Chip size="small" label={`Risk ${securityByUser[sid]?.riskScore ?? 0}`} sx={{ mx: 0.5 }} />
                    <Chip size="small" label={`${(securityEventsByUser[sid] || []).length} events`} sx={{ mx: 0.5 }} />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setSecurityDrawerStudent(student);
                        setSecurityDrawerOpen(true);
                      }}
                      sx={{ mx: 0.5, flexShrink: 0 }}
                    >
                      Review
                    </Button>
                  </>
                )}
                <Chip
                  size="small"
                  icon={isExcluded ? <BlockIcon sx={{ fontSize: 16 }} /> : (registrationByUser[sid]?.registered ? <HowToRegIcon sx={{ fontSize: 16 }} /> : <PersonIcon sx={{ fontSize: 16 }} />)}
                  label={isExcluded ? 'Excluded' : (registrationByUser[sid]?.registered ? 'Registered' : 'Not registered')}
                  color={isExcluded ? 'warning' : (registrationByUser[sid]?.registered ? 'success' : 'default')}
                  variant={isExcluded || registrationByUser[sid]?.registered ? 'filled' : 'outlined'}
                  sx={{ mx: 0.5, flexShrink: 0 }}
                  title={isExcluded ? 'Excluded from invigilation monitoring — Virgil disabled on client' : (registrationByUser[sid]?.registered ? 'User has completed face registration' : 'User has not completed face registration')}
                />
                {isExcluded ? (
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    startIcon={<DoNotDisturbOffIcon />}
                    onClick={() => handleIncludeUser(student)}
                    sx={{ ml: 0.5, flexShrink: 0 }}
                  >
                    Re-enable
                  </Button>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={<BlockIcon />}
                    onClick={() => handleOpenExcludeDialog(student)}
                    sx={{ ml: 0.5, flexShrink: 0 }}
                  >
                    Exclude
                  </Button>
                )}
                <IconButton 
                  onClick={() => handleOpenProfileModal(student)}
                  title={isStudentConnected(sid, lastHeartbeats, disconnectedUserIds) ? 'User Profile (Connected)' : 'User Profile (Disconnected)'}
                  sx={{ ml: 0.5 }}
                >
                  <Badge 
                    badgeContent={
                      isStudentConnected(sid, lastHeartbeats, disconnectedUserIds)
                        ? <CheckCircleIcon style={{ fontSize: 12, color: '#fff' }} />
                        : <CancelIcon style={{ fontSize: 12, color: '#fff' }} />
                    }
                    color={isStudentConnected(sid, lastHeartbeats, disconnectedUserIds) ? 'success' : 'error'}
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  >
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: registrationByUser[sid]?.registered ? 'success.main' : (registrationLoading ? 'grey.400' : 'grey.500'),
                        color: 'white'
                      }}
                    >
                      <PersonIcon fontSize="small" />
                    </Avatar>
                  </Badge>
                </IconButton>
                <IconButton onClick={() => handleOpenMessageDialog(student.user_id ?? student.id)}>
                  <MessageIcon />
                </IconButton>
                <IconButton onClick={() => handleOpenTranscriptModal(student)}>
                  <Badge color="secondary" variant="dot" invisible={!studentTranscripts[sid] || studentTranscripts[sid].length === 0}>
                    <AudiotrackIcon />
                  </Badge>
                </IconButton>
                <IconButton onClick={() => { setSelectedStudent(student); setViewModalOpen(true); }}>
                  <VisibilityIcon />
                </IconButton>
                <IconButton onClick={() => handleOpenReportModal(student)}>
                  <DescriptionIcon />
                </IconButton>
                <div style={{ marginLeft: 12, fontSize: 12, color: '#555' }} title="ok = successful face checks; fail = Face Verification Failed violations received">
                  Verify: <b>{(verifyCounts[sid]?.ok || 0)}</b> ok / <b>{violations.filter(v => String(v.userId ?? v.user_id) === sid && v.type === 'Face Verification Failed').length}</b> fail
                  {snapshotUploads[sid] && (
                    <>
                      {' '}| Snapshots: <span style={{ color: snapshotUploads[sid].fail > 0 ? '#f44336' : '#4caf50' }}>
                        <b>{snapshotUploads[sid].success}</b>✓ / <b>{snapshotUploads[sid].fail}</b>✗
                      </span>
                    </>
                  )}
                </div>
              </ListItem>
                </Fragment>
              );
              })}
              {students.length === 0 && (
                <ListItem>
                  <ListItemText primary="No students found." />
                </ListItem>
              )}
        </List>
                      )}
                    </Paper>
      )}
      {activeTab === 1 && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, alignItems: 'center' }}>
            <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
              Student violations
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={violationsLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={fetchViolationsFromApi}
              disabled={violationsLoading}
            >
              Refresh from server
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip
              label={`${violationSummary.total} total violation${violationSummary.total === 1 ? '' : 's'}`}
              color={violationSummary.total > 0 ? 'error' : 'default'}
              variant={violationSummary.total > 0 ? 'filled' : 'outlined'}
            />
            <Chip
              label={`${violationSummary.withViolations} student${violationSummary.withViolations === 1 ? '' : 's'} with violations`}
              color={violationSummary.withViolations > 0 ? 'warning' : 'default'}
              variant="outlined"
            />
          </Box>
          <TextField
            size="small"
            placeholder="Filter by student name, ID, email, or violation type…"
            value={violationStudentFilter}
            onChange={(e) => setViolationStudentFilter(e.target.value)}
            sx={{ mb: 2, minWidth: 280 }}
            fullWidth
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Expand a student to see their violation history. Counts update live as events arrive.
          </Typography>
          {studentViolationRows.filter((r) => r.count > 0 || violationStudentFilter.trim()).length === 0 ? (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {violations.length === 0
                  ? 'No violations recorded yet. They appear here per student as they happen.'
                  : 'No students match your filter.'}
              </Typography>
            </Paper>
          ) : (
            studentViolationRows
              .filter((r) => r.count > 0 || violationStudentFilter.trim())
              .map((row) => (
                <Accordion key={row.userId} disableGutters sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%', pr: 1 }}>
                      <Badge badgeContent={row.count} color="error" max={9999} showZero>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: row.count > 0 ? 'error.light' : 'grey.400' }}>
                          <PersonIcon fontSize="small" />
                        </Avatar>
                      </Badge>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {row.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID {row.userId}{row.email ? ` · ${row.email}` : ''}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={`${row.count} violation${row.count === 1 ? '' : 's'}`}
                        color={row.count > 0 ? 'error' : 'default'}
                        variant={row.count > 0 ? 'filled' : 'outlined'}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    {renderStudentViolationsTable(row.violations, { studentLabel: row.name })}
                  </AccordionDetails>
                </Accordion>
              ))
          )}
        </Paper>
      )}
      {activeTab === 2 && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, alignItems: 'center' }}>
            <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
              Violation & transcript audit (room {library_id})
            </Typography>
            <Button
              size="small"
              startIcon={<DeleteSweepIcon />}
              color="warning"
              variant="outlined"
              onClick={handleClearSessionAudit}
            >
              Clear session audit
            </Button>
            <Button
              size="small"
              startIcon={<FileDownloadIcon />}
              variant="outlined"
              onClick={() =>
                downloadAuditCsv(
                  buildAuditExportPayload(violations, students, studentTranscripts),
                  auditFilenameBase
                )
              }
            >
              CSV
            </Button>
            <Button
              size="small"
              startIcon={<FileDownloadIcon />}
              variant="outlined"
              onClick={() =>
                downloadAuditXlsx(
                  buildAuditExportPayload(violations, students, studentTranscripts),
                  auditFilenameBase
                )
              }
            >
              Excel
            </Button>
            <Button
              size="small"
              startIcon={<FileDownloadIcon />}
              variant="contained"
              onClick={() =>
                downloadAuditPdf(
                  buildAuditExportPayload(violations, students, studentTranscripts),
                  `Proctoring audit — exam ${library_id}`,
                  auditFilenameBase
                )
              }
            >
              PDF
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Export violation and transcript audit data. Per-student breakdown is on the <strong>Violations</strong> tab.
            {' '}
            <strong>Clear session audit</strong> removes in-memory data only; refresh may reload from the server.
          </Typography>
          {violationSummary.withViolations > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Summary by student
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Student</TableCell>
                      <TableCell>ID</TableCell>
                      <TableCell align="right">Violations</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {studentViolationRows
                      .filter((r) => r.count > 0)
                      .slice(0, 10)
                      .map((row) => (
                        <TableRow key={row.userId}>
                          <TableCell>{row.name}</TableCell>
                          <TableCell>{row.userId}</TableCell>
                          <TableCell align="right">
                            <Chip size="small" label={row.count} color="error" />
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Paper>
      )}
      {/* Violation snapshot modal */}
      <Dialog open={snapshotModalOpen} onClose={handleCloseSnapshotModal} maxWidth="md" fullWidth>
        <DialogTitle>Violation snapshot</DialogTitle>
        <DialogContent>
          {snapshotModalData && (
            <>
              {snapshotModalData.studentLabel && (
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  {snapshotModalData.studentLabel}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {snapshotModalData.type}
                {snapshotModalData.timestamp != null && (
                  <> · {formatViolationTime(snapshotModalData.timestamp)}</>
                )}
              </Typography>
              {snapshotModalData.details && (
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {snapshotModalData.details}
                </Typography>
              )}
              <Box sx={{ textAlign: 'center', bgcolor: '#f5f5f5', borderRadius: 1, p: 1, minHeight: 200 }}>
                {snapshotImgError ? (
                  <Typography variant="body2" color="error" sx={{ py: 4 }}>
                    Could not load snapshot image.
                  </Typography>
                ) : (
                  <Box
                    component="img"
                    src={snapshotModalData.url}
                    alt={`Snapshot — ${snapshotModalData.type}`}
                    sx={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 1, objectFit: 'contain' }}
                    onError={() => setSnapshotImgError(true)}
                  />
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {snapshotModalData?.url && (
            <Button
              href={snapshotModalData.url}
              target="_blank"
              rel="noopener noreferrer"
              color="inherit"
            >
              Open in new tab
            </Button>
          )}
          <Button onClick={handleCloseSnapshotModal} color="primary">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Student Video Feed Modal */}
      <Dialog open={viewModalOpen} onClose={() => setViewModalOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Student Video Feed</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="textSecondary">
            Admin examId (library_id): {library_id}
            {selectedStudent && (
              <> | Student examId: {selectedStudent.exam_id || selectedStudent.examId || selectedStudent.examID || 'N/A'}</>
            )}
                  </Typography>
          {selectedStudent ? (
            <Box sx={{ p: 2 }}>
              <Typography variant="h6">{selectedStudent.first_name} {selectedStudent.last_name} (ID: {selectedStudent.user_id})</Typography>
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {(() => {
                  const userIdStr = String(selectedStudent.user_id);
                  const webcamFrame = studentFrames[userIdStr];
                  const screenFrame = studentScreenFrames[userIdStr];
                  const panes = [
                    { title: 'Webcam', frame: webcamFrame, keyName: userIdStr },
                    { title: 'Screen share', frame: screenFrame, keyName: `screen-${userIdStr}` }
                  ];
                  return panes.map((pane) => (
                    <Box key={pane.title} sx={{ flex: '1 1 360px', minWidth: 280 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>{pane.title}</Typography>
                      <Box sx={{ width: '100%', height: 280, bgcolor: '#222', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 2, position: 'relative', overflow: 'hidden' }}>
                        {pane.frame ? (
                          <CanvasFrameRenderer
                            userId={pane.keyName}
                            frameData={pane.frame}
                            frameKey={frameKeys[pane.keyName] || 0}
                          />
                        ) : (
                          <Typography variant="body2" sx={{ px: 2 }}>
                            {pane.title === 'Screen share'
                              ? 'No screen share yet. Student must complete Closed Book screen share.'
                              : 'No webcam feed yet. Student must allow camera and stay connected.'}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ));
                })()}
              </Box>
                </Box>
          ) : null}
            </DialogContent>
            <DialogActions>
          <Button onClick={() => setViewModalOpen(false)} color="primary">Close</Button>
            </DialogActions>
      </Dialog>
      <Dialog open={messageDialogOpen} onClose={handleCloseMessageDialog}>
        <DialogTitle>Send Message to Student</DialogTitle>
        <DialogContent>
            <TextField
            autoFocus
            margin="dense"
            label="Message"
            type="text"
              fullWidth
            value={messageText}
            onChange={e => setMessageText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMessageDialog}>Cancel</Button>
          <Button onClick={handleSendMessage} variant="contained" color="primary" disabled={!messageText.trim()}>Send</Button>
        </DialogActions>
      </Dialog>
      {/* Transcript Modal */}
      <Dialog open={transcriptModalOpen} onClose={handleCloseTranscriptModal} maxWidth="sm" fullWidth>
        <DialogTitle>Audio Transcript</DialogTitle>
        <DialogContent>
          <Typography variant="h6">
            {transcriptStudent ? `${transcriptStudent.first_name} ${transcriptStudent.last_name} (ID: ${transcriptStudent.user_id})` : ''}
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, minHeight: 100, maxHeight: 300, overflow: 'auto', mt: 2 }}>
            {transcriptStudent && studentTranscripts[transcriptStudent.user_id] && Array.isArray(studentTranscripts[transcriptStudent.user_id]) ? (
              studentTranscripts[transcriptStudent.user_id].map((entry, idx) => (
                <div key={idx} style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: idx < studentTranscripts[transcriptStudent.user_id].length - 1 ? '1px solid #eee' : 'none' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
                    {entry.transcript}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {new Date(entry.timestamp).toLocaleString()}
                  </Typography>
                </div>
              ))
            ) : (
              <Typography variant="body2" color="textSecondary">
                No transcript available.
              </Typography>
            )}
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTranscriptModal} color="primary">Close</Button>
        </DialogActions>
      </Dialog>
      {/* User Profile Modal */}
      <Dialog open={profileModalOpen} onClose={handleCloseProfileModal} maxWidth="md" fullWidth>
        <DialogTitle>
          Student Profile
          {profileStudent && ` - ${profileStudent.first_name} ${profileStudent.last_name}`}
        </DialogTitle>
        <DialogContent>
          {profileLoading ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>Loading profile...</Typography>
            </Box>
          ) : profileData ? (
            <>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                Registration Status
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: profileData.registration?.registered ? '#e8f5e9' : '#ffebee' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {profileData.registration?.registered ? (
                    <>
                      <CheckCircleIcon sx={{ color: '#4caf50' }} />
                      <Typography variant="body1" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                        ✅ Registered for Proctoring
                      </Typography>
                    </>
                  ) : (
                    <>
                      <CancelIcon sx={{ color: '#f44336' }} />
                      <Typography variant="body1" sx={{ color: '#c62828', fontWeight: 'bold' }}>
                        ❌ Not Registered
                      </Typography>
                    </>
                  )}
                </Box>
                {profileData.registration?.registeredAt && (
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                    Registered: {new Date(profileData.registration.registeredAt).toLocaleString()}
                  </Typography>
                )}
              </Paper>

              {profileData.registration?.registered && (
                <>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                    Reference Face Image
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, mb: 2, textAlign: 'center' }}>
                    {profileData.registration.s3_url || profileData.registration.referenceFace ? (
                      <img 
                        src={profileData.registration.s3_url || profileData.registration.referenceFace} 
                        alt="Reference Face" 
                        style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '8px' }}
                      />
                    ) : (
                      <Typography color="textSecondary">No reference image available</Typography>
                    )}
                    {profileData.registration.stored_in && (
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                        Storage: {profileData.registration.stored_in}
                      </Typography>
                    )}
                  </Paper>
                </>
              )}

              {profileData.idDocument && (
                <>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                    ID Document
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    {profileData.idDocument.s3_url || profileData.idDocument.id_image_data ? (
                      <img 
                        src={profileData.idDocument.s3_url || profileData.idDocument.id_image_data} 
                        alt="ID Document" 
                        style={{ maxWidth: '100%', borderRadius: '8px', marginBottom: '16px' }}
                      />
                    ) : (
                      <Typography color="textSecondary">No ID image available</Typography>
                    )}
                    
                    {profileData.idDocument.ocr_text && (
                      <>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
                          OCR Extracted Text:
                        </Typography>
                        <Paper sx={{ p: 2, backgroundColor: '#f5f5f5', mb: 2 }}>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '12px' }}>
                            {profileData.idDocument.ocr_text}
                          </Typography>
                        </Paper>
                      </>
                    )}
                    
                    {(profileData.idDocument.id_number || profileData.idDocument.full_name || profileData.idDocument.date_of_birth) && (
                      <>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
                          Extracted Information:
                        </Typography>
                        <Box sx={{ pl: 2 }}>
                          {profileData.idDocument.id_number && (
                            <Typography variant="body2">• ID Number: <strong>{profileData.idDocument.id_number}</strong></Typography>
                          )}
                          {profileData.idDocument.full_name && (
                            <Typography variant="body2">• Full Name: <strong>{profileData.idDocument.full_name}</strong></Typography>
                          )}
                          {profileData.idDocument.date_of_birth && (
                            <Typography variant="body2">• Date of Birth: <strong>{profileData.idDocument.date_of_birth}</strong></Typography>
                          )}
                        </Box>
                      </>
                    )}
                    
                    {profileData.idDocument.ocr_confidence && (
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 2 }}>
                        OCR Confidence: {profileData.idDocument.ocr_confidence}%
                      </Typography>
                    )}
                  </Paper>
                </>
              )}
              
              {!profileData.registration?.registered && !profileData.idDocument && (
                <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', backgroundColor: '#fff3e0' }}>
                  <Typography variant="body1" color="textSecondary">
                    ⚠️ Student has not completed registration yet
                  </Typography>
                </Paper>
              )}
            </>
          ) : (
            <Typography color="error">Failed to load profile data</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProfileModal} color="primary">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Exam Report Modal */}
      <Dialog open={reportModalOpen} onClose={handleCloseReportModal} maxWidth="md" fullWidth>
        <DialogTitle>Exam Report</DialogTitle>
        <DialogContent>
          {reportStudent && (
            <>
              <Typography variant="h6" gutterBottom>
                {reportStudent.first_name} {reportStudent.last_name} (ID: {reportStudent.user_id})
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Violation history for this learner is on the <strong>Violations</strong> tab (export CSV / Excel / PDF on Audit).
              </Typography>
              <Typography variant="subtitle1">Transcript</Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 4, minHeight: 100, maxHeight: 200, overflow: 'auto' }}>
                {reportStudent && studentTranscripts[reportStudent.user_id] && Array.isArray(studentTranscripts[reportStudent.user_id]) ? (
                  studentTranscripts[reportStudent.user_id].map((entry, idx) => (
                    <div key={idx} style={{ marginBottom: '8px' }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {entry.transcript}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                        {new Date(entry.timestamp).toLocaleString()}
                      </Typography>
                    </div>
                  ))
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No audio detected yet...
                  </Typography>
                )}
              </Paper>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReportModal} color="primary">Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={excludeDialogOpen} onClose={handleCloseExcludeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Exclude from invigilation monitoring</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This will immediately stop all Virgil monitoring and client-side proctoring features for{' '}
            <strong>
              {excludeTarget ? `${excludeTarget.first_name || ''} ${excludeTarget.last_name || ''}`.trim() : 'this student'}
            </strong>
            . The student can continue their exam without being monitored.
          </Typography>
          <TextField
            label="Reason (optional)"
            value={excludeReason}
            onChange={(e) => setExcludeReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            placeholder="e.g. Approved accommodation, technical issue, admin override"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseExcludeDialog} disabled={excludeSubmitting}>Cancel</Button>
          <Button onClick={handleConfirmExclude} color="warning" variant="contained" disabled={excludeSubmitting}>
            {excludeSubmitting ? 'Excluding…' : 'Exclude from monitoring'}
          </Button>
        </DialogActions>
      </Dialog>
      <Drawer
        anchor="right"
        open={securityDrawerOpen}
        onClose={() => setSecurityDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, p: 2 } }}
      >
        <Typography variant="h6" gutterBottom>
          Security review
        </Typography>
        {securityDrawerStudent && (
          <>
            <Typography variant="subtitle1">
              {securityDrawerStudent.first_name} {securityDrawerStudent.last_name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              ID: {securityDrawerStudent.user_id ?? securityDrawerStudent.id}
            </Typography>
            {(() => {
              const sid = String(securityDrawerStudent.user_id ?? securityDrawerStudent.id ?? securityDrawerStudent.learner_id ?? '');
              const status = securityByUser[sid] || {};
              const events = securityEventsByUser[sid] || [];
              return (
                <>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Status: <b>{status.securityStatus || '—'}</b> · Risk: <b>{status.riskScore ?? 0}</b>
                    <br />
                    Fullscreen: {status.fullscreen ? 'yes' : 'no'} · Screen: {status.screenShare ? 'yes' : 'no'} · Visible: {status.pageVisible ? 'yes' : 'no'}
                  </Typography>
                  {events.length === 0 ? (
                    <Typography color="text.secondary">No security events yet.</Typography>
                  ) : (
                    events.map((e) => (
                      <Box key={e.eventId || `${e.type}-${e.timestamp}`} sx={{ mb: 1.5, pb: 1.5, borderBottom: '1px solid #eee' }}>
                        <Typography variant="caption" color="text.secondary">
                          {e.timestamp ? new Date(e.timestamp).toLocaleTimeString() : ''}
                        </Typography>
                        <Typography variant="body2">
                          <b>{e.type}</b> · {String(e.severity || '').toUpperCase()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">{e.message}</Typography>
                        {e.metadata && e.metadata.awayDurationMs != null && (
                          <Typography variant="caption">Duration: {(e.metadata.awayDurationMs / 1000).toFixed(1)} seconds</Typography>
                        )}
                      </Box>
                    ))
                  )}
                </>
              );
            })()}
          </>
        )}
      </Drawer>
    </Box>
  );
};

export default AdminDashboard; 